import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_BASE || '/api';
const POLL_MS = 5 * 60 * 1000; // 5 min, matches API cache TTL

// Fetches the AI-generated solar briefing (headline + deck + impacts), the
// 72h NOAA Kp forecast, the live NOAA-scale conditions (G/R/S + flare), and
// the upstream timestamp on the Cumiana Schumann spectrogram. Returns `null`
// for missing pieces while loading or on failure.
export function useSolarBriefing(enabled = true) {
  const [briefing,   setBriefing]   = useState(null);
  const [forecast,   setForecast]   = useState(null);
  const [conditions, setConditions] = useState(null);
  const [schumann,   setSchumann]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const controller = new AbortController();

    async function fetchBriefing() {
      setLoading(true);
      try {
        const r = await fetch(`${API}/ai/solar-briefing`, {signal:controller.signal});
        if (!r.ok) throw new Error(`briefing ${r.status}`);
        const data = await r.json();
        if (!cancelled) { setBriefing(data); setError(null); }
      } catch (e) {
        if (!cancelled) { setError(e.message); setBriefing(prev=>prev ? {...prev,status:'stale'} : null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    async function fetchForecast() {
      try {
        const r = await fetch(`${API}/solar-forecast`, {signal:controller.signal});
        if (!r.ok) throw new Error(`source ${r.status}`);
        const data = await r.json();
        if (!cancelled) setForecast(data);
      } catch { if (!cancelled) setForecast(prev=>prev ? {...prev,status:'stale'} : null); }
    }

    async function fetchConditions() {
      try {
        const r = await fetch(`${API}/solar-conditions`, {signal:controller.signal});
        if (!r.ok) throw new Error(`source ${r.status}`);
        const data = await r.json();
        if (!cancelled) setConditions(data);
      } catch { if (!cancelled) setConditions(prev=>prev ? {...prev,status:'stale'} : null); }
    }

    // HEAD the proxied Schumann spectrogram to learn the upstream timestamp
    // (X-Upstream-Last-Modified) and whether the proxy is serving fresh data.
    // We don't fetch the image body here, only the metadata, so the chip
    // can render an "updated Xm ago" hint without preloading 220KB.
    async function fetchSchumannMeta() {
      try {
        const r = await fetch(`${API}/schumann/spectrogram`, { method: 'HEAD',signal:controller.signal });
        if (!r.ok) throw new Error(`source ${r.status}`);
        const upstreamLastModified = r.headers.get('x-upstream-last-modified');
        const cacheState           = r.headers.get('x-cached'); // hit|miss|stale
        if (!cancelled) {
          setSchumann({
            upstreamLastModified,
            cacheState,
            fetchedAt: new Date().toISOString(),
          });
        }
      } catch { if (!cancelled) setSchumann(prev=>prev ? {...prev,cacheState:'stale'} : null); }
    }

    fetchBriefing();
    fetchForecast();
    fetchConditions();
    fetchSchumannMeta();

    const ids = [
      setInterval(fetchBriefing,      POLL_MS),
      setInterval(fetchForecast,      POLL_MS),
      setInterval(fetchConditions,    POLL_MS),
      setInterval(fetchSchumannMeta,  POLL_MS),
    ];
    return () => {
      cancelled = true;
      controller.abort();
      ids.forEach(clearInterval);
    };
  }, [enabled]);

  return { briefing, forecast, conditions, schumann, loading, error };
}
